import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import DownloadIcon from 'mdi-react/DownloadIcon'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { getShopOrdesAPI } from '@/api/shopApi'
import { useAppContext } from '@/App.context'
import Page from '@/components/content/Page'
import { FilterInput } from '@/components/form/Input'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useText, useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import Hits from '@/components/primitives/Hits'
import ProductVisualizer from '@/components/ProductVisualizer'
import Highlight from '@/components/util/Highlight'
import LoadWrapper from '@/components/util/LoadWrapper'
import {
  getFilteredOrders,
  getTextFilter,
  setOrders,
  setTextFilter,
  getNumberOfOrders,
  getCsvExportData,
} from '@/ducks/shopOrdersReducer'
import useRenderLimit from '@/hooks/render-limit-hook'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useLocalDate } from '@/hooks/use-local-date'
import useApiCaller from '@/hooks/useApiCaller'
import { csvExport } from '@/util/csv-export'
import { appRoutes } from '@/util/misc'

import StatusSelect from './StatusSelect'
import StatusTabs from './StatusTabs'

import './shop-orders-page.scss'

const ShopOrdersPage = () => {
  const { whiteLabel } = useAppContext()
  const dispatch = useDispatch()
  const orders = useSelector(getFilteredOrders)
  const textFilter = useSelector(getTextFilter)
  const numberOfResults = useSelector(getNumberOfOrders)
  const csvExportData = useSelector(getCsvExportData)
  const { formatDate } = useLocalDate()

  const searchPlaceholderText = useText('supersearch...')

  const { visibleList, ListLoaderComponent } = useRenderLimit(orders)

  const { callApi, isLoading } = useApiCaller({
    // @ts-ignore
    action: getShopOrdesAPI,
    showProgressPopup: false,
  })

  useEffect(() => {
    callApi().then(result => {
      dispatch(setOrders(result))
    })
  }, [])

  return (
    <Page className="shop-orders-page">
      <h1>
        <Translation id="Shop Orders" />
      </h1>

      <LoadWrapper isLoading={isLoading}>
        <div className="shop-order-filter">
          <StatusTabs />

          <StatusSelect />

          <FilterInput
            type="text"
            placeholder={searchPlaceholderText}
            value={textFilter}
            onChange={e => dispatch(setTextFilter(e.target.value))}
          />

          <Button
            onClick={() =>
              csvExport(csvExportData, `shop-orders_${formatDate({ date: new Date() })}.csv`)
            }
            Icon={<DownloadIcon />}
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
          >
            <Translation id="Export data" />
          </Button>

          <Hits amount={numberOfResults} />
        </div>

        {visibleList.length > 0 && (
          <table className="shop-order-table">
            <thead>
              <tr>
                <th className="center">
                  <Translation id="ID" />
                </th>
                <th />
                <th>
                  <Translation id="User" />
                </th>
                <th>
                  <Translation id="Status" />
                </th>
                <th>
                  <Translation id="payments" />
                </th>
                <th className="number">
                  <Translation id="Cost" />
                </th>
                <th>
                  <Translation id="Orderdate" />
                </th>
                <th>
                  <Translation id="Delivery date" />
                </th>
              </tr>
            </thead>

            <tbody>
              {visibleList.map(order => (
                <Order key={order.id} {...order} />
              ))}
            </tbody>
          </table>
        )}

        <ListLoaderComponent />
      </LoadWrapper>
    </Page>
  )
}

export default ShopOrdersPage

type Product = { model: string; price: number; imgUrl: string }
type ShopOrder = {
  id: number
  order_date: string
  status: number
  delivery_date: string | null
  down_payments: number
  remainder_amount: number
  cover_amount: number
  customer_name: string
  vendor_name: string
  quote_name: string | null
  quote_id: number | null
  cost: number
  codes: string
  users: string
  products: Product[]
  currency: string
  vendor_sales_order_no: string
  dealer_comment: string
  internal_comment: string
  isInManualProcessing: boolean
  isInAutomaticProcessing: boolean
  isLockedForEditing: boolean
  org_no: string
}

const Order: FC<ShopOrder & { statusText: string }> = ({
  id,
  order_date,
  statusText,
  users,
  codes,
  cost,
  delivery_date,
  remainder_amount,
  down_payments,
  customer_name,
  org_no,
  products,
  currency,
  vendor_sales_order_no,
  dealer_comment,
  internal_comment,
  isInAutomaticProcessing,
  isInManualProcessing,
}) => {
  const textFilter = useSelector(getTextFilter)
  const { formatDate } = useLocalDate()
  const { formatNumber } = useFormattedNumberWithCurrency()
  const translate = useTranslation()

  const [isCommentVisible, setIsCommentVisible] = useState(false)

  const hasComment = Boolean(dealer_comment?.trim()) || Boolean(internal_comment?.trim())

  return (
    <tr>
      <td className="order-id">
        <Link
          to={appRoutes.shopOrder.replace(':order_id', String(id))}
          onMouseOver={() => setIsCommentVisible(true)}
          onMouseOut={() => setIsCommentVisible(false)}
        >
          <Highlight pattern={textFilter} text={String(id)} />
        </Link>

        {vendor_sales_order_no && (
          <div className="vendor-sales-order-no">
            <Highlight pattern={textFilter} text={vendor_sales_order_no} />
          </div>
        )}

        {hasComment && (
          <div className={`dealer-comment ${isCommentVisible ? 'is-visible' : 'is-hidden'}`}>
            {!!dealer_comment?.trim() && (
              <>
                <h4>
                  <Translation id="Customer comment" />
                </h4>
                <p>{dealer_comment}</p>
              </>
            )}

            {!!internal_comment?.trim() && (
              <>
                <h4>
                  <Translation id="Your comment" />
                </h4>
                <p>{internal_comment}</p>
              </>
            )}
          </div>
        )}
      </td>

      <td>
        {(isInAutomaticProcessing || isInManualProcessing) && (
          <div
            data-tooltip-id={`order-automation-indicator-${id}`}
            data-tooltip-content={translate(
              isInAutomaticProcessing
                ? 'This order is an automated order.'
                : 'This order is for manual handling.'
            )}
            className={twJoin(
              'bold flex h-7 w-7 items-center justify-center rounded-full text-lg font-semibold text-white',
              isInManualProcessing && 'bg-skytech-lightOrange',
              isInAutomaticProcessing && 'bg-green-600'
            )}
          >
            <div>{isInAutomaticProcessing ? 'A' : 'M'}</div>
          </div>
        )}
        <Tooltip id={`order-automation-indicator-${id}`} style={{ zIndex: 20 }} size="large" />
      </td>

      <td className="user-info">
        {users?.split(',').map((userName, i) => {
          const code = codes?.split(',')[i]

          return (
            <div key={userName + i}>
              <div className="customer_name">
                <Highlight
                  pattern={textFilter}
                  text={org_no ? customer_name + ' - ' + org_no : customer_name}
                />
              </div>

              <div className="name">
                <Highlight pattern={textFilter} text={userName} />
              </div>

              <div className="code">
                <Highlight pattern={textFilter} text={code} />
              </div>
            </div>
          )
        })}
      </td>

      <td>
        <Highlight pattern={textFilter} text={statusText} />
      </td>

      <td>
        {remainder_amount === 0 ? (
          ''
        ) : (
          <>
            <div className="dp_mth">
              <Translation id="n months" n={String(down_payments)} />
            </div>
            <div className="payment-size">
              <Translation
                id="n/m"
                n={formatNumber({ amount: remainder_amount / down_payments, currency })}
              />
            </div>
          </>
        )}
      </td>

      <td className="number">
        <div>
          <FormattedNumberWithCurrency amount={cost} currency={currency} />
        </div>
        <div className="remainder">
          <FormattedNumberWithCurrency amount={remainder_amount} currency={currency} />
        </div>
      </td>

      <td className="number">
        <Highlight pattern={textFilter} text={formatDate({ date: order_date }) || ''} />
      </td>

      <td className="number">
        <Highlight pattern={textFilter} text={formatDate({ date: delivery_date }) || ''} />
      </td>

      <td>
        <ProductVisualizer products={products} />
      </td>
    </tr>
  )
}
